/**
 * Kaplan–Meier survival curves, Greenwood intervals and the log-rank test.
 *
 * Every number follows R's `survival` package (`survfit`, `survdiff`):
 *
 * - S(t) = Π (1 − dᵢ/nᵢ) over event times ≤ t, with nᵢ subjects at risk (time ≥ tᵢ).
 * - Greenwood: Var[ln S(t)] = Σ dᵢ / (nᵢ (nᵢ − dᵢ)); `se` is S(t) times its square root,
 *   which is what `summary(survfit)` prints as std.err.
 * - Interval types (`conf.type`): "log" S·exp(±z·s) with the upper bound capped at 1;
 *   "log-log" S^exp(±z·s/|ln S|); "plain" S ± z·S·s clamped to [0, 1].
 * - Median: the first time S(t) ≤ 0.5, or the midpoint of that time and the next event
 *   time when S sits exactly on 0.5 for an interval. Its limits are the first times the
 *   lower and upper interval curves reach 0.5 (null, "not reached", if they never do).
 * - Log-rank (Mantel–Haenszel form of `survdiff`, rho = 0): at each distinct event time the
 *   expected deaths in group j are d·nⱼ/n and the hypergeometric covariance is
 *   d·(nⱼ/n)(δⱼₖ − nₖ/n)(n − d)/(n − 1). χ² = (O − E)′ V⁻¹ (O − E) on the first k − 1
 *   groups, with k − 1 degrees of freedom.
 */

import { chiSquareUpper, normalQuantile } from "@/lib/stats/distributions";

export type IntervalType = "log" | "log-log" | "plain";

export interface Observation {
  time: number;
  event: boolean;
}

export interface SurvivalRow {
  time: number;
  atRisk: number;
  events: number;
  censored: number;
  survival: number;
  /** Greenwood standard error of S(t); NaN once S(t) reaches zero. */
  se: number;
  lower: number;
  upper: number;
}

export interface MedianEstimate {
  /** Null when the curve never falls to 0.5. */
  value: number | null;
  lower: number | null;
  upper: number | null;
}

export interface SurvivalCurve {
  n: number;
  events: number;
  rows: SurvivalRow[];
  median: MedianEstimate;
  /** Largest observed time. */
  maxTime: number;
}

const HALF_TOLERANCE = 1e-10;

function bounds(
  survival: number,
  logSe: number,
  type: IntervalType,
  z: number
): [number, number] {
  if (!(survival > 0 && Number.isFinite(logSe))) {
    return survival === 1 ? [1, 1] : [Number.NaN, Number.NaN];
  }
  if (type === "log") {
    return [
      survival * Math.exp(-z * logSe),
      Math.min(1, survival * Math.exp(z * logSe)),
    ];
  }
  if (type === "log-log") {
    if (survival === 1) {
      return [1, 1];
    }
    const spread = logSe / Math.abs(Math.log(survival));
    return [
      survival ** Math.exp(z * spread),
      survival ** Math.exp(-z * spread),
    ];
  }
  const half = z * survival * logSe;
  return [Math.max(0, survival - half), Math.min(1, survival + half)];
}

function medianFrom(
  rows: readonly SurvivalRow[],
  curve: (row: SurvivalRow) => number,
  midpoint: boolean
): number | null {
  const index = rows.findIndex((row) => curve(row) <= 0.5 + HALF_TOLERANCE);
  if (index < 0) {
    return null;
  }
  const row = rows[index];
  if (midpoint && Math.abs(curve(row) - 0.5) <= HALF_TOLERANCE) {
    const next = rows
      .slice(index + 1)
      .find((other) => curve(other) < 0.5 - HALF_TOLERANCE);
    return next ? (row.time + next.time) / 2 : row.time;
  }
  return row.time;
}

/** Event times only: the curve and median only change there. */
function eventRows(rows: readonly SurvivalRow[]): SurvivalRow[] {
  return rows.filter((row) => row.events > 0);
}

export function kaplanMeier(
  observations: readonly Observation[],
  type: IntervalType = "log-log",
  level = 0.95
): SurvivalCurve {
  const z = normalQuantile(1 - (1 - level) / 2);
  const times = [...new Set(observations.map((o) => o.time))].sort(
    (a, b) => a - b
  );
  let atRisk = observations.length;
  let survival = 1;
  let greenwood = 0;
  const rows: SurvivalRow[] = [];
  for (const time of times) {
    let events = 0;
    let censored = 0;
    for (const o of observations) {
      if (o.time === time) {
        if (o.event) {
          events += 1;
        } else {
          censored += 1;
        }
      }
    }
    if (events > 0) {
      survival *= 1 - events / atRisk;
      greenwood +=
        atRisk > events ? events / (atRisk * (atRisk - events)) : Number.NaN;
    }
    const logSe = Math.sqrt(greenwood);
    const [lower, upper] = bounds(survival, logSe, type, z);
    rows.push({
      time,
      atRisk,
      events,
      censored,
      survival,
      se: survival > 0 ? survival * logSe : Number.NaN,
      lower,
      upper,
    });
    atRisk -= events + censored;
  }
  const medianRows = eventRows(rows);
  return {
    n: observations.length,
    events: observations.filter((o) => o.event).length,
    rows,
    maxTime: times.at(-1) ?? 0,
    median: {
      value: medianFrom(medianRows, (row) => row.survival, true),
      lower: medianFrom(medianRows, (row) => row.lower, false),
      upper: medianFrom(medianRows, (row) => row.upper, false),
    },
  };
}

/** The curve's value at time t (1 before the first event). */
export function survivalAt(curve: SurvivalCurve, time: number): number {
  let value = 1;
  for (const row of curve.rows) {
    if (row.time > time) {
      break;
    }
    value = row.survival;
  }
  return value;
}

/**
 * The row in force at time t, as `summary(survfit, times = t)` reads it: the last row at or
 * before t, or S = 1 with no uncertainty before the first time. Null beyond follow-up.
 */
export function rowAt(curve: SurvivalCurve, time: number): SurvivalRow | null {
  if (time > curve.maxTime) {
    return null;
  }
  let current: SurvivalRow | null = null;
  for (const row of curve.rows) {
    if (row.time > time) {
      break;
    }
    current = row;
  }
  if (current) {
    return current;
  }
  const atRisk = curve.rows[0]?.atRisk ?? curve.n;
  return {
    time,
    atRisk,
    events: 0,
    censored: 0,
    survival: 1,
    se: 0,
    lower: 1,
    upper: 1,
  };
}

/** Subjects still under observation at time t: those with an observed time ≥ t. */
export function atRiskAt(
  observations: readonly Observation[],
  time: number
): number {
  return observations.filter((o) => o.time >= time).length;
}

export interface LogRankResult {
  groups: number;
  chiSquare: number;
  df: number;
  p: number;
  observed: number[];
  expected: number[];
  sizes: number[];
}

/** Solve A x = b by Gaussian elimination with partial pivoting; null if singular. */
function solve(matrix: number[][], vector: number[]): number[] | null {
  const k = vector.length;
  const a = matrix.map((row, i) => [...row, vector[i]]);
  for (let col = 0; col < k; col++) {
    let pivot = col;
    for (let r = col + 1; r < k; r++) {
      if (Math.abs(a[r][col]) > Math.abs(a[pivot][col])) {
        pivot = r;
      }
    }
    if (Math.abs(a[pivot][col]) < 1e-12) {
      return null;
    }
    [a[col], a[pivot]] = [a[pivot], a[col]];
    for (let r = 0; r < k; r++) {
      if (r !== col) {
        const factor = a[r][col] / a[col][col];
        for (let c = col; c <= k; c++) {
          a[r][c] -= factor * a[col][c];
        }
      }
    }
  }
  return a.map((row, i) => row[k] / row[i]);
}

/** The log-rank test across two or more groups; null if it cannot be computed. */
export function logRank(
  groups: readonly (readonly Observation[])[]
): LogRankResult | null {
  const k = groups.length;
  if (k < 2) {
    return null;
  }
  const times = [
    ...new Set(
      groups.flatMap((g) => g.filter((o) => o.event).map((o) => o.time))
    ),
  ].sort((a, b) => a - b);
  const observed = new Array<number>(k).fill(0);
  const expected = new Array<number>(k).fill(0);
  const covariance = Array.from({ length: k }, () =>
    new Array<number>(k).fill(0)
  );
  for (const time of times) {
    const atRisk = groups.map((g) => g.filter((o) => o.time >= time).length);
    const deaths = groups.map(
      (g) => g.filter((o) => o.time === time && o.event).length
    );
    const n = atRisk.reduce((s, v) => s + v, 0);
    const d = deaths.reduce((s, v) => s + v, 0);
    for (let j = 0; j < k; j++) {
      observed[j] += deaths[j];
      expected[j] += (d * atRisk[j]) / n;
    }
    if (n > 1) {
      const factor = (d * (n - d)) / (n * n * (n - 1));
      for (let j = 0; j < k; j++) {
        for (let l = 0; l < k; l++) {
          const base =
            j === l
              ? atRisk[j] * n - atRisk[j] * atRisk[l]
              : -atRisk[j] * atRisk[l];
          covariance[j][l] += factor * base;
        }
      }
    }
  }
  const diff = observed.slice(0, k - 1).map((o, j) => o - expected[j]);
  const reduced = covariance.slice(0, k - 1).map((row) => row.slice(0, k - 1));
  const solved = solve(reduced, diff);
  if (!solved) {
    return null;
  }
  const chiSquare = diff.reduce((sum, value, j) => sum + value * solved[j], 0);
  return {
    groups: k,
    chiSquare,
    df: k - 1,
    p: chiSquareUpper(chiSquare, k - 1),
    observed,
    expected,
    sizes: groups.map((g) => g.length),
  };
}
